<template>
  <main>
    <loader v-if="!pageLoaded || loading" />
    <div v-if="pageLoaded">
      <div class="bg-white border border-[#F0F0F0] rounded-xl p-6 mb-7">
        <h2 class="text-[18px] text-brand-black leading-[150%] font-bold mb-4">
          Find over 21,000 qualified talents <br />
          to hire for your company
        </h2>
        <div class="flex space-x-3 items-center">
          <div class="relative w-full">
            <input
              type="search"
              class="block w-full px-4 py-3 pr-10 text-sm text-gray-900 border border-gray-300 rounded-md bg-gray-50"
              placeholder="Search talents"
              v-model="filterKeys.search"
              @change="getAllTalents"
              required
            />

            <button class="absolute right-4 top-3 z-10">
              <img src="../../assets/icons/search.svg" alt="" />
            </button>
          </div>

          <tam-button
            class="bg-brand-black text-white rounded-xl text-[14px] hover:bg-brand-black"
            label="Browse"
            color="secondary"
            :icon="Cross"
            @click="showFilter = true"
          />
        </div>
      </div>

      <div class="bg-white border border-[#F0F0F0] rounded-xl p-6 mb-7">
        <h2 class="text-[18px] text-brand-black leading-[150%] font-bold mb-1">Explore by <span class="text-brand-original">Categories</span></h2>
        <p class="text-gray-400 text-[15px] leading-[24px]">Find over 21,000 qualified talents to hire for your company</p>

        <div class="flex gap-4 items-center flex-wrap mt-4">
          <button
            value=""
            @click="
              setFilterVar($event, 'job_category', '');
              getAllTalents();
            "
            :class="[
              'inline-block px-6 py-2 text-[15px] font-manrope capitalize',
              !filterKeys['job_category']
                ? 'border border-brand-black rounded-xl text-brand-black font-bold'
                : 'rounded-xl text-gray-600 border border-gray-300',
            ]"
          >
            All
          </button>
          <button
            :value="item.key"
            @click="
              setFilterVar($event, 'job_category', item.value);
              getAllTalents();
            "
            :class="[
              'inline-block px-6 py-2 text-[15px] font-manrope capitalize',
              filterKeys['job_category'] == item.key
                ? 'border border-brand-black rounded-xl text-brand-black font-bold'
                : 'rounded-xl text-gray-500 border border-gray-300',
            ]"
            v-for="item in jobCategories"
            :key="item.key"
          >
            {{ item.value }}
          </button>
        </div>
      </div>

      <div class="grid-cols-1 grid w-full gap-5 md:grid-cols-3 mb-6 relative">
        <template v-if="activeSubsciption">
          <talent-card
            v-for="item in talents"
            :key="item.uuid"
            :talent="item"
            :has-subscription="activeSubsciption"
            @profile="
              selectedTalentId = item?.uuid;
              showTalentDetailDialog = true;
            "
            @mail="emailTalent(talent)"
            @share="shareProfile(talent)"
          />
        </template>

        <template v-else>
          <talent-card
            v-for="item in 6"
            :key="item"
            :talent="{ name: 'Talent Fullname', job_preference: { desired_salary: 0.0 }, years_of_experience: '0' }"
            :has-subscription="activeSubsciption"
          />

          <div class="absolute z-10 left-0 right-0 top-0 bottom-0 flex justify-center items-start mt-12" v-if="!activeSubsciption">
            <div class="my-9 p-4 md:p-9 bg-white w-1/2 shadow-xl rounded-xl border-gray-500">
              <div class="flex flex-col justify-between items-center">
                <div class="pt-4 flex flex-col justify-between items-center mb-2">
                  <h1 class="font-semibold text-2xl mb-3 text-brand-black text-center">Upgrade for full access</h1>
                  <span class="font-normal text-md leading-[160%] text-gray-700 text-center"
                    >You are currently using the careerbuddy platform for free. Activate your subscription to get access to talents on the careerbuddy
                    platform</span
                  >
                </div>
                <div class="pt-4">
                  <button @click="$router.push('/subscriptions')" class="bg-brand-black text-white p-2.5 px-8 rounded-xl">Continue</button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <Paginate
      :from="paginateContent.meta.from"
      :to="paginateContent.meta.to"
      :total="paginateContent.meta.total"
      :current="paginateContent.meta.current_page"
      :next="paginateContent.links.next"
      :prev="paginateContent.links.prev"
      :pages="paginateContent.meta.links"
      @move="doPaginate"
    />

    <send-talents-mail-dialog :open="showSendTalentMail" @on-close="showSendTalentMail = false" :talentDetails="talentDetails" />

    <share-profiles-dialog :open="showProfilesShareDialog" @on-close="showProfilesShareDialog = false" :talents="talents" />

    <share-profile-dialog :open="showProfileShareDialog" @on-close="showProfileShareDialog = false" :appDetails="talentDetails" />

    <talent-filter
      :open="showFilter"
      :enums="enums"
      :job-categories="jobCategories"
      :experience="level_of_experience"
      :skill-list="skillList"
      :filter="filter"
      :filterVars="filterVars"
      @update="setFilterVar"
      @search="getSkills"
      @on-apply="getAllTalents"
      @on-close="closeFilter"
      @on-clear="clearFilter"
    />

    <talent-details-dialog
      v-if="showTalentDetailDialog"
      :talent-id="selectedTalentId"
      @close="
        selectedTalentId = null;
        showTalentDetailDialog = false;
      "
    />
  </main>
</template>

<script setup>
import TamButton from '../TamButton.vue';
import { FilterLine, Cross, Search, More } from '@/icons';
import moment from 'moment';
import Loader from '@/components/LoadingOverlay.vue';
import { ref, onMounted, computed } from 'vue';
import { initFlowbite } from 'flowbite';
import Paginate from '@/components/Paginate.vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import SendTalentsMailDialog from '@/components/talent/SendTalentsMail.vue';
import ShareProfilesDialog from './ShareProfile.vue';
import ShareProfileDialog from '@/components/applications/ShareProfile';
import TalentFilter from './Filter.vue';
import TalentCard from './TalentCard.vue';
import { alert } from '@/utils/helper';
import TalentDetailsDialog from '@/components/talent/TalentDetailsDialog.vue';

const store = useStore();
const router = useRouter();
const tableDetails = ref([' ', 'Name', 'Field', 'Availability', 'Date Joined', 'Action']);
const pageLoaded = ref(false);
const loading = ref(false);
const showFilter = ref(false);
const showActions = ref(false);
const showSendTalentMail = ref(false);
const checkbox = ref(true);
const showProfileShareDialog = ref(false);
const showProfilesShareDialog = ref(false);
const showTalentDetailDialog = ref(false);
const talentDetails = ref(null);
const selectedTalentId = ref(null);
const level_of_experience = ref([
  { key: 'entry_level', value: 'Entry level' },
  { key: 'junior_level', value: 'Junior level' },
  { key: 'mid_level', value: 'Mid level' },
  { key: 'senior_level', value: 'Senior level' },
  { key: 'lead', value: 'Lead' },
  { key: 'executive', value: 'Executive' },
]);
const subscriptions = ref([]);
const skillList = ref([]);

const filterVars = ref([]);
const filter = ref({
  role_title: '',
  job_type: '',
  salary_currency: '',
  salary_min: '',
  salary_max: '',
  job_category: '',
  level_of_experience: '',
  search: '',
  skills: '',
});

const filterKeys = ref({
  role_title: '',
  job_type: '',
  salary_currency: '',
  salary_min: '',
  salary_max: '',
  job_category: '',
  level_of_experience: '',
  search: '',
  skills: '',
});

const activeSubsciption = computed(() => {
  // return false
  return subscriptions.value.filter((item) => item.subscription_status === 'active').length >= 1;
});

const talents = computed(() => {
  return store.getters['global/getAllTalents'];
});

const jobCategories = computed(() => {
  return store.getters['global/getJobCategories'];
});

const enums = computed(() => {
  return store.getters['global/getEnumDetails'];
});

// Add new job, if talent is empty
const openJob = () => {
  router.push('/jobs');
};

const doPaginate = async ({ params }) => {
  await store.dispatch('global/getAllTalents', { params });
};

const paginateContent = computed(() => {
  return store.getters['global/getTalentsPayload'];
});

const closeFilter = () => {
  showFilter.value = false;
};

const setFilterVar = async (event, key, value = '') => {
  if (event?.target?.type != 'checkbox') {
    filterKeys.value[key] = event == 'tag-select' ? value : event.target.value;
    filter.value[key] = value ? value : event.target.value;
  } else {
    filterKeys.value[key] = event.target.checked ? event.target.value : '';
    filter.value[key] = event.target.checked && value ? value : '';
  }

  const selected = Object.values(filter.value).filter((val) => val !== '') || [];
  filterVars.value = selected;
};

const removeFilterItem = async (index) => {
  Object.entries(filter.value).filter(([k, v]) => {
    if (filterVars.value[index] == v) {
      filter.value[k] = '';
      filterKeys.value[k] = '';
      return;
    }
  });

  filterVars.value.splice(index, 1);
  await getFilteredTalents();
};

const clearFilter = async (index) => {
  Object.entries(filter.value).filter(([k, v]) => {
    filter.value[k] = '';
    filterKeys.value[k] = '';
  });

  filterVars.value = [];
  await getAllTalents();
};

const getAllTalents = async () => {
  const params = Object.entries(filterKeys.value).reduce((a, [k, v]) => (v == '' ? a : ((a[k] = v), a)), {});

  try {
    loading.value = true;
    await store.dispatch('global/getAllTalents', { params });
  } catch (error) {
  } finally {
    showFilter.value = false;
    loading.value = false;
  }
};

const getSubscriptionPayments = async () => {
  loading.value = true;
  try {
    const { data } = await store.dispatch('global/getSubscriptionPayments');
    subscriptions.value = data;
  } catch (error) {
    alert(errorMessage(error), {
      timeout: 3000,
      hideProgressBar: true,
    });
  } finally {
    loading.value = false;
  }
};

const shareProfile = (talent) => {
  talentDetails.value = talent;
  showProfileShareDialog.value = true;
};

const emailTalent = (talent) => {
  talentDetails.value = talent;
  showSendTalentMail.value = true;
};

const getSkills = async (query = '') => {
  try {
    const skills = await store.dispatch('global/getSkills', query);
    skillList.value = skills.map((item) => item.name);
  } catch (error) {}
};

onMounted(async () => {
  await Promise.all([
    getSubscriptionPayments(),
    getAllTalents(),
    store.dispatch('global/getJobCategories'),
    store.dispatch('global/getEnums'),
    getSkills(),
  ]);
  pageLoaded.value = true;
  initFlowbite();
});
</script>
